/**
 * scheduleGridShared.ts
 * Constants + pure date helpers shared by the Machine Schedule grid and its
 * extracted sub-components (header row, etc.). Keeps each file under the
 * 600-line cap (CODING-STANDARDS.md).
 */

/** Width of the frozen left (machine) column, px. */
export const LEFT_COL_WIDTH = 180;
/** Height of a machine row, px. */
export const ROW_HEIGHT = 48;
/** Height of a machine row when compact (one-line "code · name") labels are on, px. */
export const COMPACT_ROW_HEIGHT = 28;
/** Minimum width of a day column, px. */
export const DAY_COL_MIN = 38;
/**
 * Theme-spacing padding of SchedulePage's content column — the Schedule
 * section's ONE scroll container. Sticky headers inside it pin to its CONTENT
 * edge, so a flush pin needs `top: theme.spacing(-SCHEDULE_CONTENT_PADDING)`
 * (Phase1Panel task table). Lives here (not SchedulePage) to avoid an import
 * cycle SchedulePage → …Panel → SchedulePage.
 */
export const SCHEDULE_CONTENT_PADDING = 3;

export function dayAbbrev(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-CA', { weekday: 'short' });
}
export function dayNum(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-CA', { day: 'numeric' });
}
export function monthAbbrev(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-CA', { month: 'short' });
}
export function isWeekend(isoDate: string): boolean {
  const d = new Date(`${isoDate}T00:00:00`).getDay();
  return d === 0 || d === 6;
}
/**
 * True when `isoDate` falls on a statutory holiday.
 * `holidays` may be the raw `holidaysInRange` array from
 * GET /api/schedule/working-days ({ date, name }[]) or a plain ISO-string list.
 * The lab is closed on stat holidays, so these days get the non-work shading
 * the same way weekends do — see MachineScheduleGrid + ScheduleGantt plotBands.
 */
export function isStatHoliday(
  isoDate: string,
  holidays: { date: string }[] | string[],
): boolean {
  for (const h of holidays) {
    const hDate = typeof h === 'string' ? h : h.date;
    // Tolerate datetime values ('2026-07-01T00:00:00…') by comparing the date part.
    if (hDate.slice(0, 10) === isoDate) return true;
  }
  return false;
}
export function isToday(isoDate: string): boolean {
  return isoDate === new Date().toISOString().slice(0, 10);
}

/** Array of ISO date strings for every day in [startDate, endDate] inclusive. */
export function buildDayRange(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  const cur = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  while (cur <= end) {
    days.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

/**
 * Left + width fraction (0-1) for an arbitrary date range within the window,
 * clamped to the window. Null when the range misses the window entirely.
 * (Moved from MachineScheduleGrid.tsx - 600-line cap.)
 */
export function computeRangePosition(
  startISO: string,
  endISO: string,
  windowStart: string,
  windowEnd: string,
  totalDays: number,
): { leftFraction: number; widthFraction: number } | null {
  const bStart = new Date(`${startISO}T00:00:00`);
  const bEnd = new Date(`${endISO}T00:00:00`);
  const wStart = new Date(`${windowStart}T00:00:00`);
  const wEnd = new Date(`${windowEnd}T00:00:00`);
  if (bEnd < wStart || bStart > wEnd) return null;

  const clampedStart = bStart < wStart ? wStart : bStart;
  const clampedEnd = bEnd > wEnd ? wEnd : bEnd;
  const msPerDay = 86_400_000;
  const startDayIndex = Math.round((clampedStart.getTime() - wStart.getTime()) / msPerDay);
  const spanDays = Math.round((clampedEnd.getTime() - clampedStart.getTime()) / msPerDay) + 1;
  return { leftFraction: startDayIndex / totalDays, widthFraction: spanDays / totalDays };
}

/** computeRangePosition for a booking's own StartDate/EndDate. */
export function computeBarPosition(
  booking: { StartDate: string; EndDate: string },
  windowStart: string,
  windowEnd: string,
  totalDays: number,
): { leftFraction: number; widthFraction: number } | null {
  return computeRangePosition(booking.StartDate, booking.EndDate, windowStart, windowEnd, totalDays);
}
